import { DrawingToolHelpers } from "../../helpers/drawing-tool-helpers";
import { StringHelpers } from "../../helpers/string-helpers";
import { IComponent } from "../component.interface";
import { IDrawingTool } from "./drawing-tool.interface";
import { DrawingToolsManager } from "./drawing-tools.manager";

export class NewTextBoxComponent implements IComponent, IDrawingTool {
    public id = '';

    public annotation: any = null;
    private drawingToolManager: DrawingToolsManager;

    constructor(drawingToolManager: DrawingToolsManager, id: string) {
        this.drawingToolManager = drawingToolManager;
        this.id = id;
    }

    public static syncCreate(drawingToolManager: DrawingToolsManager, annotationOptions: any): NewTextBoxComponent {
        const chart: any = drawingToolManager.base.tiChartSeries.highStockChart;

        annotationOptions.labels[0].point.x = DrawingToolHelpers.calculateXPosition(
            annotationOptions.labels[0].point.actualXValue,
            annotationOptions.labels[0].point.actualStartTimeT,
            drawingToolManager);

        const component = new NewTextBoxComponent(drawingToolManager, annotationOptions.id);
        const addedNewAnnotation: any = chart.addAnnotation(annotationOptions);

        drawingToolManager.updateAnnotationStyles(addedNewAnnotation, annotationOptions, false);
        component.annotation = addedNewAnnotation;
        return component;
    }

    public static create(drawingToolManager: DrawingToolsManager, event: any): NewTextBoxComponent {
        const id = StringHelpers.newGuid();
        const series = drawingToolManager.base.tiChartSeries;
        const builder = drawingToolManager.base.tiChartSeries.highChartBuilder;
        const chart: any  = drawingToolManager.base.tiChartSeries.highStockChart;
        
        const coords = chart.pointer.getCoordinates(event)
        const coordsX = chart.navigationBindings.utils.getAssignedAxis(coords.xAxis)
        const coordsY = chart.navigationBindings.utils.getAssignedAxis(coords.yAxis)
       
        const langKey = 'customLabel';

        const chartX = event.chartX;
        const chartY = event.chartY;
        const x = chart.xAxis[0].toValue(chartX);
        const y = chart.yAxis[0].toValue(chartY);

        const opt: any = {
            langKey: langKey,
            // type: 'basicAnnotation',
            labelOptions: {
                allowOverlap: true,
                format: 'Add Text',
            },
            labels: [{
                // text: 'Add Text',
                shape: "rectable",
                point: {
                    xAxis: coordsX.axis.options.index,
                    yAxis: coordsY.axis.options.index,
                    lastDataPointXValue: drawingToolManager.base.tiChartSeries.lastDataPoint?.xValue,
                    originalY: chartY,
                    x: x,
                    y: y
                },
                style: {
                    fontSize: '11px',
                    color: '#fff'
                }
            }],
            events: {
                add: function (event) {
                    if (event.target?.options) {
                        event.target.options.id = id;
                    }
                    // me.saveAnnotations();
                    series.onAnnotationAdd(event, false);
                },
                afterUpdate: function (event) {
                    if (event.target?.options && !event.target.options.id) {
                        event.target.options.id = id;
                    }
                    const afterDrawingToolCompleted = event && event.target && !event.target.isUpdating;
                    if (afterDrawingToolCompleted) {
                        if (event.target?.options?.typeOptions?.points?.length) {
                            for (const p of event.target.options.typeOptions.points) {
                                p.actualStartTimeT = null;
                                p.actualXValue = null;
                            }
                        }
                        
                        drawingToolManager.saveAnnotations();
                    }
                    builder.drawingToolRibbonUpdate(() => {
                        series.onAnnotationAfterUpdate(event);
                    });
                },
                remove: function () {
                    drawingToolManager.delete();
                }
            }
        };

        const existingSetting = drawingToolManager.drawingToolsSetting.get(langKey);
        if (existingSetting?.labelOptions?.backgroundColor) {
            opt.labelOptions.backgroundColor = existingSetting.labelOptions.backgroundColor;
        }

        if (existingSetting?.labelOptions?.style) {
            opt.labelOptions.style = {
                color: existingSetting.labelOptions.style.color,
                fontSize: existingSetting.labelOptions.style.fontSize
            };
        }
        
        const annotation = chart.addAnnotation(opt);

        if (existingSetting?.labelOptions?.style) {
            drawingToolManager.updateAnnotationStyles(annotation, annotation.options, false);
        }
        
        const component = new NewTextBoxComponent(drawingToolManager, id);
        component.annotation = annotation;
        return component;
    }
    
    public syncUpdate(annotationOptions: any) {
        if (!annotationOptions?.labels?.[0].point) {
            return;
        }

        annotationOptions.labels[0].point.x = DrawingToolHelpers.calculateXPosition(
            annotationOptions.labels[0].point.actualXValue,
            annotationOptions.labels[0].point.actualStartTimeT,
            this.drawingToolManager);

            const shouldUpdate =  this.annotation.options.labels[0].point.x != annotationOptions.labels[0].point.x ||
            this.annotation.options.labels[0].point.y != annotationOptions.labels[0].point.y ||
            this.annotation.userOptions.labels[0].x != annotationOptions.labels[0].x ||
            this.annotation.userOptions.labels[0].y != annotationOptions.labels[0].y ||
            this.annotation.options.labelOptions.format != annotationOptions.labelOptions.format;

        if (shouldUpdate) {
            this.annotation.update(annotationOptions);
        } else {
            this.annotation.options.labelOptions.backgroundColor = annotationOptions.labelOptions.backgroundColor;
            this.annotation.options.labelOptions.style.fontSize = annotationOptions.labelOptions.style.fontSize;
            this.annotation.options.labelOptions.style.color = annotationOptions.labelOptions.style.color;

            this.annotation.userOptions.labelOptions.backgroundColor = annotationOptions.labelOptions.backgroundColor;
            if ( this.annotation.userOptions.labelOptions.style && annotationOptions.labelOptions.style) {
                this.annotation.userOptions.labelOptions.style.fontSize = annotationOptions.labelOptions.style.fontSize;
                this.annotation.userOptions.labelOptions.style.color = annotationOptions.labelOptions.style.color;
            }
        }
        
        this.drawingToolManager.updateAnnotationStyles(this.annotation, annotationOptions, false);
    }

    public getMetaData(): any {
        if (!this.annotation.options?.labels?.length) {
            return null;
        }

        const startPoint = this.annotation.options.labels[0].point;
        const actualStartPosition = DrawingToolHelpers.getActualStartTimePosition(startPoint, this.drawingToolManager);

        return {
            id: this.id,
            langKey: 'customLabel',
            labelOptions: {
                allowOverlap: true,
                format: this.annotation.options.labelOptions.format,
                backgroundColor: this.annotation.options.labelOptions.backgroundColor,
                style: {
                    color: this.annotation.options.labelOptions.style.color,
                    fontSize: this.annotation.options.labelOptions.style.fontSize
                }
            },
            labels: [{
                shape: "rectable",
                point: {
                    xAxis: this.annotation.options.labels[0].point.xAxis,
                    yAxis: this.annotation.options.labels[0].point.yAxis,
                    
                    actualStartTimeT: actualStartPosition?.actualStartTimeT,
                    actualXValue: actualStartPosition?.actualXValue,

                    x: this.annotation.options.labels[0].point.x,
                    y: this.annotation.options.labels[0].point.y,
                },
                x: this.annotation.userOptions.labels[0].x,
                y: this.annotation.userOptions.labels[0].y,
                style: {
                    color: this.annotation.options.labelOptions.style.color,
                    fontSize: this.annotation.options.labelOptions.style.fontSize
                }
            }]
        };
    }
}